import { useEffect, useId, useState } from "react";
import { CalendarClock, Repeat, Trash2 } from "lucide-react";
import type { ReminderPriority, ReminderQuadrant } from "@lockcal/shared";
import { useAppStore } from "@/store/useAppStore";
import { useLiveRecords } from "@/hooks/useDomain";
import type { ReminderRecord } from "@/lib/entities";
import { newReminder, newReminderList, touch } from "@/lib/records";
import { formatDate } from "@/lib/date";
import { parseRRule } from "@/lib/recurrence";
import { isRecurring, recentCompletions } from "@/lib/reminders";
import {
  combineDateAndTime,
  nextRoundTime,
  toDateInput,
  toTimeInput,
} from "@/lib/datetime";
import { Dialog } from "@/ui/Dialog";
import { Button } from "@/ui/Button";
import { Field, Input, Select, LabelledInput, Textarea } from "@/ui/Field";
import { toast } from "@/ui/Toast";
import { cn } from "@/lib/cn";

type RepeatMode = "none" | "daily" | "every" | "weekly" | "monthly";

/** ISO weekday (1 = Monday … 7 = Sunday) → RRULE day code. */
const ISO_TO_RRULE = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];
const WEEKDAY_CHIPS = [
  { iso: 1, label: "T2" },
  { iso: 2, label: "T3" },
  { iso: 3, label: "T4" },
  { iso: 4, label: "T5" },
  { iso: 5, label: "T6" },
  { iso: 6, label: "T7" },
  { iso: 7, label: "CN" },
];

const PRIORITIES = [
  { value: 0, label: "Thấp" },
  { value: 1, label: "Bình thường" },
  { value: 2, label: "Cao" },
  { value: 3, label: "Khẩn" },
];

const QUADRANTS = [
  { value: 0, label: "Làm ngay" },
  { value: 1, label: "Lên kế hoạch" },
  { value: 2, label: "Giao việc" },
  { value: 3, label: "Sau này" },
];

const ALERTS = [
  { value: 0, label: "Không nhắc" },
  { value: 5, label: "5 phút trước" },
  { value: 15, label: "15 phút trước" },
  { value: 60, label: "1 giờ trước" },
  { value: 1440, label: "1 ngày trước" },
];

export function ReminderDialog({
  open,
  onClose,
  reminder,
  defaultDate,
}: {
  open: boolean;
  onClose: () => void;
  reminder?: ReminderRecord | null;
  /** Day to prefill when creating (defaults to today). */
  defaultDate?: number;
}) {
  const formId = useId();
  const upsert = useAppStore((s) => s.upsert);
  const remove = useAppStore((s) => s.remove);
  const lists = useLiveRecords<{ id: string; title: string }>("reminderLists");

  const [title, setTitle] = useState("");
  const [listId, setListId] = useState("");
  const [hasDue, setHasDue] = useState(true);
  const [dueDate, setDueDate] = useState("");
  const [dueTime, setDueTime] = useState("17:00");
  const [priority, setPriority] = useState<ReminderPriority>(1);
  const [quadrant, setQuadrant] = useState<ReminderQuadrant>(0);
  const [alert, setAlert] = useState(0);
  const [repeatMode, setRepeatMode] = useState<RepeatMode>("none");
  const [repeatEvery, setRepeatEvery] = useState(7);
  const [repeatDays, setRepeatDays] = useState<number[]>([]);
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);

    if (reminder) {
      setTitle(reminder.title ?? "");
      setListId(reminder.listId ?? "");
      setHasDue(reminder.dueEpochMillis !== null);
      const due = reminder.dueEpochMillis ?? nextRoundTime();
      setDueDate(toDateInput(due));
      setDueTime(toTimeInput(due));
      setPriority(reminder.priority ?? 0);
      setQuadrant(reminder.quadrant ?? 0);
      setAlert(reminder.alertMinutesBefore ?? 0);
      setNotes(reminder.notes ?? "");
      const parts = parseRRule(reminder.rrule);
      if ((reminder.repeatEveryDays ?? 0) > 0) {
        setRepeatMode("every");
        setRepeatEvery(reminder.repeatEveryDays ?? 7);
        setRepeatDays([]);
      } else if (parts?.freq === "DAILY") {
        setRepeatMode("daily");
        setRepeatDays([]);
      } else if (parts?.freq === "WEEKLY") {
        setRepeatMode("weekly");
        setRepeatDays((parts.byDay ?? []).map((day) => (day === 0 ? 7 : day)));
      } else if (parts?.freq === "MONTHLY") {
        setRepeatMode("monthly");
        setRepeatDays([]);
      } else {
        setRepeatMode("none");
        setRepeatDays([]);
      }
    } else {
      const due = nextRoundTime(defaultDate ?? Date.now());
      setTitle("");
      setListId(lists[0]?.id ?? "");
      setHasDue(true);
      setDueDate(toDateInput(due));
      setDueTime("17:00");
      setPriority(1);
      setQuadrant(0);
      setAlert(0);
      setRepeatMode("none");
      setRepeatEvery(7);
      setRepeatDays([]);
      setNotes("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reminder?.id]);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    const cleanTitle = title.trim();
    if (!cleanTitle) {
      setError("Đặt tên cho việc cần làm trước đã.");
      return;
    }

    const due = hasDue ? combineDateAndTime(dueDate, dueTime) : null;
    if (hasDue && due === null) {
      setError("Hạn chót không hợp lệ.");
      return;
    }

    let rrule: string | null = null;
    let repeatEveryDays: number | null = null;
    if (hasDue && repeatMode !== "none") {
      if (repeatMode === "daily") {
        rrule = "FREQ=DAILY";
      } else if (repeatMode === "every") {
        repeatEveryDays = Math.max(
          1,
          Math.min(365, Math.round(repeatEvery) || 1),
        );
      } else if (repeatMode === "weekly") {
        if (repeatDays.length === 0) {
          setError("Chọn ít nhất một ngày trong tuần.");
          return;
        }
        const codes = [...repeatDays]
          .sort((a, b) => a - b)
          .map((iso) => ISO_TO_RRULE[iso % 7])
          .join(",");
        rrule = `FREQ=WEEKLY;BYDAY=${codes}`;
      } else {
        const monthDay = Number(dueDate.slice(8, 10)) || 1;
        rrule = `FREQ=MONTHLY;BYMONTHDAY=${monthDay}`;
      }
    }

    setBusy(true);
    try {
      let targetList = listId;
      if (!targetList) {
        const created = newReminderList("Cá nhân");
        await upsert("reminderLists", created);
        targetList = created.id;
      }

      const patch = {
        listId: targetList,
        title: cleanTitle,
        notes: notes.trim() || null,
        dueEpochMillis: due,
        priority,
        quadrant,
        alertMinutesBefore: alert || null,
        rrule,
        repeatEveryDays,
        ...(rrule || repeatEveryDays
          ? { isCompleted: false, completedAtEpochMillis: null }
          : {}),
      };

      if (reminder) {
        const record = useAppStore
          .getState()
          .data.reminders.find((item) => item.id === reminder.id);
        if (record) await upsert("reminders", touch({ ...record, ...patch }));
        toast({ title: "Đã lưu việc", variant: "success" });
      } else {
        await upsert(
          "reminders",
          newReminder({ ...patch, listId: targetList }),
        );
        toast({ title: "Đã thêm việc", variant: "success" });
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Không lưu được.");
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!reminder) return;
    const record = reminder;

    await remove("reminders", reminder.id);
    onClose();
    toast({
      title: "Đã xoá việc",
      description: record.title,
      action: {
        label: "Hoàn tác",
        onClick: () => {
          void upsert("reminders", touch({ ...record, deletedAt: null }));
        },
      },
    });
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={reminder ? "Sửa việc" : "Việc mới"}
      size="md"
      footer={
        <>
          {reminder && (
            <Button
              variant="ghost"
              onClick={() => void handleDelete()}
              className="mr-auto text-danger hover:bg-dangerSoft hover:text-danger"
            >
              <Trash2 size={14} />
              Xoá
            </Button>
          )}
          <Button onClick={onClose}>Huỷ</Button>
          <Button type="submit" form={formId} variant="primary" loading={busy}>
            {reminder ? "Lưu thay đổi" : "Thêm việc"}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} className="space-y-4">
        <Field label="Việc cần làm" htmlFor={`${formId}-title`}>
          <Input
            id={`${formId}-title`}
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ví dụ: Gửi báo giá cho khách"
          />
        </Field>

        <Field label="Danh sách" htmlFor={`${formId}-list`}>
          <Select
            id={`${formId}-list`}
            value={listId}
            onChange={(e) => setListId(e.target.value)}
          >
            {lists.length === 0 && <option value="">Cá nhân (tạo mới)</option>}
            {lists.map((list) => (
              <option key={list.id} value={list.id}>
                {list.title}
              </option>
            ))}
          </Select>
        </Field>

        <div className="space-y-2">
          <label className="flex cursor-pointer items-center gap-2 text-[12px] font-medium text-ink-soft">
            <input
              type="checkbox"
              checked={hasDue}
              onChange={(e) => setHasDue(e.target.checked)}
              className="h-3.5 w-3.5 accent-[rgb(var(--c-ink))]"
            />
            Có hạn chót
          </label>
          {hasDue && (
            <div className="flex gap-2">
              <Input
                type="date"
                value={dueDate}
                className="min-w-0 flex-1"
                onChange={(e) => setDueDate(e.target.value)}
                aria-label="Ngày hết hạn"
              />
              <div className="w-[104px] shrink-0">
                <Input
                  type="time"
                  value={dueTime}
                  onChange={(e) => setDueTime(e.target.value)}
                  aria-label="Giờ hết hạn"
                />
              </div>
            </div>
          )}
        </div>

        <div className="rounded-xl border border-line bg-shell/50 px-3 py-2.5">
          <div className="flex items-center gap-2">
            <Repeat size={14} className="shrink-0 text-ink-faint" />
            <div className="min-w-0 flex-1">
              <p className="text-[12px] font-medium">Lặp lại</p>
              <p className="text-[10px] text-ink-faint">
                {hasDue
                  ? "Việc định kỳ tự xuất hiện khi tới hạn"
                  : "Cần hạn chót để lặp lại"}
              </p>
            </div>
            <div className="w-[150px] shrink-0">
              <Select
                aria-label="Lặp lại"
                value={repeatMode}
                disabled={!hasDue}
                onChange={(e) => setRepeatMode(e.target.value as RepeatMode)}
              >
                <option value="none">Không lặp</option>
                <option value="daily">Hằng ngày</option>
                <option value="every">Mỗi N ngày</option>
                <option value="weekly">Hằng tuần</option>
                <option value="monthly">Hằng tháng</option>
              </Select>
            </div>
          </div>

          {hasDue && repeatMode === "every" && (
            <div className="mt-2.5 flex flex-wrap items-center gap-2 pl-6">
              <span className="text-[11px] text-ink-soft">Mỗi</span>
              <div className="w-[68px] shrink-0">
                <Input
                  type="number"
                  min={1}
                  max={365}
                  value={repeatEvery}
                  aria-label="Số ngày lặp lại"
                  onChange={(e) => setRepeatEvery(Number(e.target.value))}
                />
              </div>
              <span className="text-[11px] text-ink-soft">
                ngày, tính từ lần làm gần nhất
              </span>
            </div>
          )}

          {hasDue && repeatMode === "weekly" && (
            <div className="mt-2.5 flex flex-wrap gap-1 pl-6">
              {WEEKDAY_CHIPS.map(({ iso, label }) => {
                const active = repeatDays.includes(iso);
                return (
                  <button
                    key={iso}
                    type="button"
                    aria-pressed={active}
                    onClick={() =>
                      setRepeatDays((days) =>
                        active
                          ? days.filter((day) => day !== iso)
                          : [...days, iso],
                      )
                    }
                    className={cn(
                      "h-7 min-w-[34px] rounded-md px-2 text-[11px] font-medium transition",
                      active
                        ? "bg-ink text-paper"
                        : "bg-paper text-ink-soft hover:text-ink",
                    )}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}

          {hasDue && repeatMode === "monthly" && (
            <p className="mt-2.5 flex items-center gap-1.5 pl-6 text-[11px] text-ink-soft">
              <CalendarClock size={12} />
              Vào ngày {Number(dueDate.slice(8, 10)) || 1} hằng tháng
            </p>
          )}
        </div>

        {reminder && isRecurring(reminder) && (
          <div className="flex items-center justify-between gap-3 rounded-xl border border-line bg-shell/50 px-3 py-2.5">
            <div className="min-w-0">
              <p className="text-[12px] font-medium">Lịch sử</p>
              <p className="truncate text-[10px] text-ink-faint">
                {(() => {
                  const history = recentCompletions(reminder, 200);
                  return history.length === 0
                    ? "Chưa làm lần nào"
                    : `Đã làm ${history.length} lần · lần cuối ${formatDate(history[0])}`;
                })()}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {Array.from({ length: 7 }, (_, index) => (
                <span
                  key={index}
                  className={cn(
                    "h-1.5 w-1.5 rounded-full",
                    recentCompletions(reminder, 7)[index]
                      ? "bg-ink"
                      : "bg-line",
                  )}
                />
              ))}
            </div>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Ưu tiên" htmlFor={`${formId}-priority`}>
            <Select
              id={`${formId}-priority`}
              value={priority}
              onChange={(e) => setPriority(Number(e.target.value) as ReminderPriority)}
            >
              {PRIORITIES.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Nhắc trước" htmlFor={`${formId}-alert`}>
            <Select
              id={`${formId}-alert`}
              value={alert}
              onChange={(e) => setAlert(Number(e.target.value))}
              disabled={!hasDue}
            >
              {ALERTS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <Field label="Ma trận Eisenhower" htmlFor={`${formId}-quadrant`}>
          <Select
            id={`${formId}-quadrant`}
            value={quadrant}
            onChange={(e) => setQuadrant(Number(e.target.value) as ReminderQuadrant)}
          >
            {QUADRANTS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Field>

        <LabelledInput
          label="Ghi chú"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Chi tiết thêm…"
        />

        {error && (
          <p className="rounded-lg bg-dangerSoft px-3 py-2 text-[12px] text-danger">
            {error}
          </p>
        )}
      </form>
    </Dialog>
  );
}
